import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useFormik } from "formik";

import ContainedButton from "components/layout/form/containedButton";
import CustomDatePickerInput from "components/newForm/CustomDatePickerInput";
import CustomInput from "components/newForm/CustomInput";
import OutlinedButton from "components/layout/form/outlinedButton";
import { AiOutlineCalendar, AiOutlineCheck } from "react-icons/ai";
import { BsPhone } from "react-icons/bs";
import { MdOutlineAlternateEmail } from "react-icons/md";
import { isAfter, isBefore, subYears } from "date-fns";

import {
  RequestPatchCustomersParams,
  createContact,
  requestGetContactId,
  requestPatchCustomers,
  requestSendToken,
  requestTokenValidation,
} from "../services";

import {
  ButtonCodeWrap,
  Container,
  Form,
  InputCheckWrap,
  LinkResendEmail,
  PanelContainer,
  TextCount,
  TextValidateCode,
  TextWait,
  TextWrap,
  ValidateBoxCode,
  ValidateInputIcon,
  ValidateWrap,
} from "./styles";
import { toast } from "react-toastify";

type PersonalDataProps = {
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  // socialName?: string;
  birthDate: string;
  email: string;
  phoneNumber: string;
  document: string;
  userId: string;
  customerCode: string;
};

const PersonalData: React.FC<PersonalDataProps> = ({
  editMode,
  setEditMode,
  // socialName,
  birthDate,
  email,
  phoneNumber,
  document,
  userId,
  customerCode,
}) => {
  const [loadingSave, setLoadingSave] = useState(false);
  const [date, setDate] = useState("");
  const [codeValidateEmail, setCodeVAlidateEmail] = useState("");
  const [codeValidatePhone, setCodeValidatePhone] = useState("");
  const [emailValidated, setEmailValidated] = useState(false);
  const [phoneValidated, setPhoneValidated] = useState(false);
  const [showValidateEmailButton, setShowValidateEmailButton] = useState(false);
  const [showValidatePhoneButton, setShowValidatePhoneButton] = useState(false);
  const [showBoxCodeEmail, setShowBoxCodeEmail] = useState(false);
  const [showBoxCodePhone, setShowBoxCodePhone] = useState(false);
  const [timeEmail, setTimeEmail] = useState(59);
  const [timePhone, setTimePhone] = useState(59);

  const buildValuesData = useCallback((): {
    codeValidatePhone: string;
    codeValidateEmail: string;
  } => {
    return { codeValidatePhone, codeValidateEmail };
  }, [codeValidatePhone, codeValidateEmail]);

  const formik = useFormik({
    initialValues: {
      // socialName: "",
      birthDate: "",
      email: "",
      phone: "",
    },
    onSubmit: async (values) => {
      const birthDateFormater = values?.birthDate?.split("/") || [];
      const body: RequestPatchCustomersParams = {
        // socialName: values.socialName,
        birthdate:
          birthDateFormater.length > 1
            ? `${birthDateFormater[2]}-${birthDateFormater[1]}-${birthDateFormater[0]}`
            : "",
        phone: values.phone,
        email: values.email,
        phoneToken: buildValuesData()?.codeValidatePhone,
        emailToken: buildValuesData()?.codeValidateEmail,
      };
      setLoadingSave(true);
      const response = await requestPatchCustomers(body);

      if (response?.status === 204) {
        toast.success("Alteração realizada com sucesso!");
        // localStorage.setItem(
        //   "@UltragazEmpresarial:userName",
        //   values.socialName
        // );
        setEditMode(false);
      } else {
        toast.error(
          "Houve um problema ao atualizar seus dados, tente novamente mais tarde!"
        );
      }
      setLoadingSave(false);
    },
  });

  useEffect(() => {
    formik.setFieldValue("birthDate", date);
  }, [date]);

  useEffect(() => {
    if (emailValidated) {
      setShowBoxCodeEmail(false);
      setShowValidateEmailButton(false);
    }
  }, [emailValidated]);

  useEffect(() => {
    if (phoneValidated) {
      setShowBoxCodePhone(false);
      setShowValidatePhoneButton(false);
    }
  }, [phoneValidated]);

  const getContactId = useCallback(
    async (contactType: "EMAIL" | "PHONE", contact: string) => {
      let contactId = "";

      const response = await requestGetContactId(
        contactType,
        contact,
        document,
        userId,
        customerCode
      );

      contactId = response?.contactId || "";

      if (!contactId) {
        contactId = await createContact(contactType, contact);
      }

      if (contactId) {
        await requestSendToken(contactId, document, userId, customerCode);
        if (contactType === "EMAIL") {
          setShowBoxCodeEmail(true);
        }
        if (contactType === "PHONE") {
          setShowBoxCodePhone(true);
        }
      }
    },
    [document, userId, customerCode]
  );

  const sendToken = useCallback(
    async (contactType: string) => {
      const token =
        contactType === "PHONE" ? codeValidatePhone : codeValidateEmail;
      const response = await requestTokenValidation(userId, token);
      const toastMessage = `Contato validado com sucesso! Clique em "Salvar alterações", para efetivar.`;
      if (response?.status === 202) {
        if (contactType === "PHONE") {
          setPhoneValidated(true);
          toast.warn(toastMessage);
        }
        if (contactType === "EMAIL") {
          setEmailValidated(true);
          toast.warn(toastMessage);
        }
      }
    },
    [userId, codeValidatePhone, codeValidateEmail]
  );

  const handleCancelEdit = useCallback(() => {
    // formik.setFieldValue("socialName", socialName);
    formik.setFieldValue("birthDate", birthDate);
    formik.setFieldValue("email", email);
    formik.setFieldValue("phone", phoneNumber);
  }, [birthDate, email, phoneNumber]);

  useEffect(() => {
    // formik.setFieldValue("socialName", socialName);
    formik.setFieldValue("birthDate", birthDate);
    formik.setFieldValue("email", email);
    formik.setFieldValue("phone", phoneNumber);
  }, [birthDate, email, phoneNumber]);

  useEffect(() => {
    setTimeout((): any => {
      if (!showBoxCodeEmail) return false;
      if (timeEmail === 0) return setTimeEmail(0);
      return setTimeEmail((prev) => prev - 1);
    }, 1000);
  }, [timeEmail, showBoxCodeEmail]);

  useEffect(() => {
    setTimeout((): any => {
      if (!showBoxCodePhone) return false;
      if (timePhone === 0) return setTimePhone(0);
      return setTimePhone((prev) => prev - 1);
    }, 1000);
  }, [timePhone, showBoxCodePhone]);

  const errorDate = useMemo(() => {
    const minDate = subYears(new Date(), 120);
    const maxDate = subYears(new Date(), 18);

    const dateSplit: string[] = date.split("/");
    const dateFormatted: Date = new Date(
      Number(dateSplit[2]),
      Number(dateSplit[1]) - 1,
      Number(dateSplit[0])
    );

    const isBeforeDate = isBefore(dateFormatted, minDate);
    const isAfterDate = isAfter(dateFormatted, maxDate);

    if (isBeforeDate) {
      return "Data inválida! Verifique a data informada para continuar o cadastro!";
    }

    if (isAfterDate) {
      return "Verifique a data informada! Cadastro não permitido para menores de 18 anos.";
    }
    return "";
  }, [date]);

  const minDate = useMemo(() => {
    return subYears(new Date(), 120);
  }, [subYears]);

  const maxDate = useMemo(() => {
    return subYears(new Date(), 18);
  }, [subYears]);

  return (
    <Container>
      <PanelContainer>
        <Form onSubmit={formik.handleSubmit} autoComplete="off">
          {/* <CustomInput
            disabled={!editMode}
            icon={BsPersonCircle}
            id="socialName"
            name="socialName"
            inputType="text"
            labelText="Nome social"
            msgError={formik.errors.socialName}
            onBlur={formik.handleBlur}
            value={formik.values.socialName}
            onChange={formik.handleChange}
            errorCondition={
              !!formik.errors.socialName && !!formik.touched.socialName
            }
          /> */}
          <CustomDatePickerInput
            setDate={setDate}
            disabled={!editMode}
            icon={AiOutlineCalendar}
            id="birthDate"
            name="birthDate"
            inputType="text"
            labelText="Data de nascimento"
            msgError={formik.errors.birthDate || errorDate}
            onBlur={formik.handleBlur}
            value={formik.values.birthDate}
            onChange={formik.handleChange}
            errorCondition={
              (!!formik.errors.birthDate && !!formik.touched.birthDate) ||
              !!errorDate
            }
            mask="99/99/9999"
            minDate={minDate}
            maxDate={maxDate}
          />
          <ValidateWrap>
            <InputCheckWrap>
              <CustomInput
                style={{
                  width: "100%",
                }}
                disabled={!editMode}
                icon={MdOutlineAlternateEmail}
                id="email"
                name="email"
                inputType="email"
                labelText="Email"
                msgError={formik.errors.email}
                onFocus={() =>
                  !emailValidated && setShowValidateEmailButton(true)
                }
                onBlur={formik.handleBlur}
                value={formik.values.email}
                onChange={(e) => {
                  formik.handleChange(e);
                  setShowValidateEmailButton(true);
                  setEmailValidated(false);
                  setCodeVAlidateEmail("");
                }}
                errorCondition={!!formik.errors.email && !!formik.touched.email}
              />
              <ValidateInputIcon>
                {emailValidated && <AiOutlineCheck size={22} color="#0000FF" />}
              </ValidateInputIcon>
            </InputCheckWrap>
            {showValidateEmailButton && (
              <OutlinedButton
                type="button"
                onClick={() => {
                  getContactId("EMAIL", formik.values.email);
                  setShowValidateEmailButton(false);
                }}
              >
                Validar email
              </OutlinedButton>
            )}
            {showBoxCodeEmail && (
              <ValidateBoxCode>
                <TextValidateCode>
                  O código foi enviado para o email informado
                </TextValidateCode>
                <CustomInput
                  id="emailCode"
                  name="emailCode"
                  inputType="text"
                  labelText="Inserir código"
                  value={codeValidateEmail}
                  onChange={(e) => setCodeVAlidateEmail(e.target.value)}
                />
                <TextWrap>
                  <TextCount>Não recebi o código.</TextCount>
                  {timeEmail > 0 && (
                    <TextWait>{`Aguarde 00:${
                      timeEmail > 9 ? timeEmail : `0${timeEmail}`
                    }`}</TextWait>
                  )}
                  {timeEmail === 0 && (
                    <LinkResendEmail
                      onClick={() => {
                        getContactId("EMAIL", formik.values.email);
                        setTimeEmail(59);
                      }}
                    >
                      Enviar novamente
                    </LinkResendEmail>
                  )}
                </TextWrap>
                <ButtonCodeWrap>
                  <OutlinedButton
                    type="button"
                    onClick={() => sendToken("EMAIL")}
                  >
                    Validar código
                  </OutlinedButton>
                </ButtonCodeWrap>
              </ValidateBoxCode>
            )}
          </ValidateWrap>
          <ValidateWrap>
            <InputCheckWrap>
              <CustomInput
                style={{
                  width: "100%",
                }}
                disabled={!editMode}
                icon={BsPhone}
                id="phone"
                name="phone"
                inputType="text"
                labelText="Telefone"
                msgError={formik.errors.phone}
                onFocus={() =>
                  !phoneValidated && setShowValidatePhoneButton(true)
                }
                onBlur={formik.handleBlur}
                value={formik.values.phone}
                onChange={(e) => {
                  formik.handleChange(e);
                  setShowValidatePhoneButton(true);
                  setPhoneValidated(false);
                  setCodeValidatePhone("");
                }}
                errorCondition={!!formik.errors.phone && !!formik.touched.phone}
                mask="(99) 99999-9999"
              />
              <ValidateInputIcon>
                {phoneValidated && <AiOutlineCheck size={22} color="#0000FF" />}
              </ValidateInputIcon>
            </InputCheckWrap>
            {showValidatePhoneButton && (
              <OutlinedButton
                type="button"
                onClick={() => {
                  getContactId("PHONE", formik.values.phone);
                  setShowValidatePhoneButton(false);
                }}
              >
                Validar celular
              </OutlinedButton>
            )}
            {showBoxCodePhone && (
              <ValidateBoxCode>
                <TextValidateCode>
                  O código foi enviado para o telefone informado
                </TextValidateCode>
                <CustomInput
                  id="phoneCode"
                  name="phoneCode"
                  inputType="text"
                  labelText="Inserir código"
                  value={codeValidatePhone}
                  onChange={(e) => setCodeValidatePhone(e.target.value)}
                />
                <TextWrap>
                  <TextCount>Não recebi o código.</TextCount>
                  {timePhone > 0 && (
                    <TextWait>{`Aguarde 00:${
                      timePhone > 9 ? timePhone : `0${timePhone}`
                    }`}</TextWait>
                  )}
                  {timePhone === 0 && (
                    <LinkResendEmail
                      onClick={() => {
                        getContactId("PHONE", formik.values.phone);
                        setTimePhone(59);
                      }}
                    >
                      Enviar novamente
                    </LinkResendEmail>
                  )}
                </TextWrap>
                <ButtonCodeWrap>
                  <OutlinedButton
                    type="button"
                    onClick={() => sendToken("PHONE")}
                  >
                    Validar código
                  </OutlinedButton>
                </ButtonCodeWrap>
              </ValidateBoxCode>
            )}
          </ValidateWrap>
          {editMode && (
            <ContainedButton
              type="submit"
              disabled={loadingSave || !!errorDate}
            >
              Salvar alterações
            </ContainedButton>
          )}
          {editMode && (
            <OutlinedButton
              type="button"
              onClick={() => {
                setEditMode(false);
                formik.resetForm();
                handleCancelEdit();
              }}
            >
              Cancelar edição
            </OutlinedButton>
          )}
        </Form>
      </PanelContainer>
    </Container>
  );
};

export default PersonalData;
